import { GameEngine } from '@/game/engine';
import { GamePhase, type GameState, type NPCData, type HeroState } from '@/game/types';
import { GameWebSocket, type ConnectionState } from '@/network/websocket';
import { getViewport, onThemeChanged, type TelegramWebApp } from '@/shared/telegram';
import { viewportAwarenessRadiusWorld } from '@/game/viewport-bounds';
import { heroPatchResponseToState, heroResponseToState } from './hero-mapper';
import type { HeroResponse } from '@/network/api';
import {
  wireWSHandler, sendRequestNearbyHeroes, sendRevive, sendUsePotion,
  type WSHandlerCallbacks, type RoadPOIRenderBridge,
} from './ws-bridge';
import { restoreRoadSettlementsOnEngine } from './town-mapper';

export interface SessionControllerPorts {
  telegramId: number;
  onState: (state: GameState) => void;
  onConnection: (state: ConnectionState) => void;
  onError: (message: string) => void;
  onProximity: (npc: NPCData | null) => void;
}

/** A terminal owner: one mount, one engine, one transport, one abort lifetime. */
export class GameSessionController {
  readonly engine = new GameEngine();
  readonly socket: GameWebSocket;
  private readonly abort = new AbortController();
  private disposed = false;
  private mounted = false;
  private gameplayStarted = false;
  private unsubTheme: (() => void) | null = null;
  private proximityTimer: ReturnType<typeof setInterval> | null = null;
  private nearbyTimer: ReturnType<typeof setInterval> | null = null;
  private movementTimer: ReturnType<typeof setInterval> | null = null;
  private lastNearbyPosition: { x: number; y: number } | null = null;

  constructor(private readonly ports: SessionControllerPorts, socket = new GameWebSocket()) {
    this.engine.renderer.onAssetError = () => {
      if (this.isActive()) this.fail('Game assets unavailable. Reload to load a compatible version.');
    };
    this.socket = socket;
    this.socket.setTelegramId(ports.telegramId);
    this.engine.onStateChange((state) => {
      if (!this.isActive()) return;
      ports.onState(state);
      this.updateProximity();
    });
    this.socket.onConnectionState((state) => {
      if (!this.isActive()) return;
      this.stopNearby();
      if (state === 'connected') this.startNearby();
      ports.onConnection(state);
    });
  }
  get signal(): AbortSignal { return this.abort.signal; }
  isActive = (): boolean => !this.disposed && !this.signal.aborted;

  /** Lifecycle callback is an explicit UI/data port, not a React effect owner. */
  async mount(container: HTMLElement, bootstrap: (owner: GameSessionController) => Promise<boolean>): Promise<void> {
    if (this.mounted || !this.isActive()) return;
    this.mounted = true;
    this.unsubTheme = onThemeChanged();
    this.proximityTimer = setInterval(() => this.updateProximity(), 250);
    let rendererReady = false;
    try {
      await this.engine.init(container);
      if (!this.isActive()) return;
      rendererReady = true;
      const start = await bootstrap(this);
      if (!this.isActive()) return;
      if (start) this.startGameplay();
    } catch (error) {
      if (!this.isActive()) return;
      console.error('[Session] Startup failed:', error);
      this.fail(rendererReady ? 'Cannot reach game server. Please try again later.'
        : 'Cannot initialize game renderer. Please try again later.');
    }
  }
  startGameplay(): void {
    if (!this.isActive() || this.gameplayStarted) return;
    this.gameplayStarted = true;
    this.engine.start();
    if (this.isActive()) this.socket.connect();
  }
  fail(message: string): void {
    if (!this.isActive()) return;
    this.ports.onError(message);
    this.dispose();
  }
  wire(callbacks: WSHandlerCallbacks, road: RoadPOIRenderBridge): void {
    const activeCallbacks = Object.fromEntries(Object.entries(callbacks).map(([name, callback]) => [
      name, (...args: unknown[]) => {
        if (this.isActive()) (callback as (...values: unknown[]) => void)(...args);
      },
    ])) as WSHandlerCallbacks;
    wireWSHandler(this.socket, this.engine, {
      ...activeCallbacks,
      onHeroStateReceived: (payload) => {
        if (!this.isActive()) return;
        const state = heroResponseToState(payload as unknown as HeroResponse);
        const previousKind = this.engine.gameState.hero?.excursionKind;
        if (previousKind === 'road_poi' && state.excursionKind !== 'road_poi') {
          restoreRoadSettlementsOnEngine(this.engine, road.locale, road.getRoadSettlements(), road.getBaseTownData());
        }
        this.engine.applyHeroState(state);
        if ((previousKind === 'town' || previousKind === 'road_poi') &&
          state.excursionKind !== 'town' && state.excursionKind !== 'road_poi') activeCallbacks.onExcursionEnd?.();
        activeCallbacks.onHeroStateReceived?.(payload);
      },
    }, road);
  }
  applyHeroPatch(response: HeroResponse): void {
    if (!this.isActive()) return;
    const hero = this.engine.gameState.hero;
    const state = heroPatchResponseToState(response, hero);
    if (hero) state.position = { ...hero.position };
    this.engine.applyHeroState(state);
  }
  applyEquipmentAdvice(response: HeroResponse): void {
    if (!this.isActive()) return;
    const current = this.engine.gameState.hero;
    const revision = response.equipmentState?.revision;
    if (!current || current.id !== response.id || !Number.isSafeInteger(revision) || revision! <= (current.equipmentRevision ?? 0)) return;
    const equipment = heroResponseToState({ ...response, gear: response.gear ?? response.equipment });
    // Equipment revision orders gear, not HP, XP, money, buffs or activity.
    // Derived combat stats must come from the current live hero state as well.
    this.engine.applyHeroPatch({
      equipment: equipment.equipment, inventory: equipment.inventory,
      equipmentRevision: revision,
      weaponType: equipment.weaponType, weaponName: equipment.weaponName,
      weaponRarity: equipment.weaponRarity, weaponIlvl: equipment.weaponIlvl,
      armorType: equipment.armorType, armorName: equipment.armorName,
      armorRarity: equipment.armorRarity, armorIlvl: equipment.armorIlvl,
    });
  }
  applyNamedHero(response: HeroResponse): void {
    if (!this.isActive()) return;
    this.engine.initFromServer(heroResponseToState(response), response.state);
    this.engine.setHeroName(response.name);
    this.startGameplay();
  }
  /** Bind REST commands to this owner even when an API ignores cancellation. */
  async request<T>(
    action: (signal: AbortSignal) => Promise<T>,
    onResponse: (value: T) => void,
    onError: (error: unknown) => void,
  ): Promise<void> {
    if (!this.isActive()) return;
    try {
      const value = await action(this.signal);
      if (this.isActive()) onResponse(value);
    } catch (error) {
      if (this.isActive()) onError(error);
    }
  }
  openPaymentInvoice(url: string, openInvoice: NonNullable<TelegramWebApp['openInvoice']>, refresh: (signal: AbortSignal) => Promise<HeroResponse>): void {
    if (!this.isActive()) return;
    openInvoice(url, (status) => {
      // SDK status never grants a purchase; the captured owner refreshes server state.
      if (status !== 'paid' || !this.isActive()) return;
      void this.request(refresh, (hero) => this.applyHeroPatch(hero),
        () => console.warn('[Session] Payment state refresh failed'));
    });
  }
  patchHero(fields: Partial<HeroState>): void {
    if (this.isActive()) this.engine.applyHeroPatch(fields);
  }
  revive(): boolean {
    if (!this.isActive() || this.socket.getState() !== 'connected') return false;
    sendRevive(this.socket); return true;
  }
  usePotion(): boolean {
    const hero = this.engine.gameState.hero;
    if (!this.isActive() || !hero || (hero.potions ?? 0) <= 0 || this.socket.getState() !== 'connected') return false;
    // Counts and HP change only after the server response.
    sendUsePotion(this.socket); return true;
  }
  private updateProximity(): void {
    if (!this.isActive()) return;
    const { hero, phase } = this.engine.gameState;
    const eligible = hero && hero.hp > 0 && (phase === GamePhase.Walking || phase === GamePhase.InTown)
      && hero.excursionKind !== 'town' && hero.excursionKind !== 'road_poi';
    this.ports.onProximity(eligible ? this.engine.getNearestTownNPC() : null);
  }
  private requestNearby = (): void => {
    if (!this.isActive()) return;
    const viewport = getViewport();
    sendRequestNearbyHeroes(this.socket, viewportAwarenessRadiusWorld(viewport.width, viewport.height, 1.5, this.engine.camera.zoom));
    this.lastNearbyPosition = this.engine.getHeroDisplayWorldPosition();
  };
  private startNearby(): void {
    this.requestNearby();
    this.nearbyTimer = setInterval(this.requestNearby, 5000);
    this.movementTimer = setInterval(() => {
      if (!this.isActive() || !this.lastNearbyPosition) return;
      const position = this.engine.getHeroDisplayWorldPosition();
      const viewport = getViewport();
      const threshold = viewportAwarenessRadiusWorld(viewport.width, viewport.height, 1.5, this.engine.camera.zoom) * 0.25;
      if ((position.x - this.lastNearbyPosition.x) ** 2 + (position.y - this.lastNearbyPosition.y) ** 2 >= threshold ** 2) {
        this.requestNearby();
      }
    }, 1000);
  }
  private stopNearby(): void {
    if (this.nearbyTimer !== null) clearInterval(this.nearbyTimer);
    if (this.movementTimer !== null) clearInterval(this.movementTimer);
    this.nearbyTimer = this.movementTimer = null;
    this.lastNearbyPosition = null;
  }
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.abort.abort();
    if (this.proximityTimer !== null) clearInterval(this.proximityTimer);
    this.proximityTimer = null;
    this.stopNearby();
    this.unsubTheme?.(); this.unsubTheme = null;
    this.socket.disconnect();
    this.engine.destroy();
  }
}
